import {useState, useRef, useCallback, useEffect} from 'react';
import {API} from '@chekin/api';
import {useSyncRef} from '@chekin/core';
import type {ServerDetectionResponse} from '../types';
import {parseDetectionResponse} from '../utils/socket';
import {useReconnect} from './useReconnect';
import {IS_DEV_MODE} from '../../../utils/devMode';

export interface UseFaceDetectionSocketProps {
  onMessage?: (response: ServerDetectionResponse) => void;
  autoConnect?: boolean;
  createSocket?: () => WebSocket;
}

export const useFaceDetectionSocket = ({
  onMessage,
  autoConnect = true,
  createSocket = () => API.webSocket.connectLivenessWebSocket(),
}: UseFaceDetectionSocketProps = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  const onMessageRef = useSyncRef(onMessage);
  const autoConnectRef = useSyncRef(autoConnect);
  const createSocketRef = useSyncRef(createSocket);
  const manualCloseRef = useRef(false);
  const connectRef = useRef<() => void>(() => {});

  const handleGiveUp = useCallback(
    () => setError(new Error('Face detection socket reconnection failed')),
    [],
  );
  const {schedule, reset: resetReconnect} = useReconnect({onGiveUp: handleGiveUp});

  const connect = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.close();
    }

    try {
      manualCloseRef.current = false;
      setIsConnecting(true);
      setError(null);

      const socket = createSocketRef.current();
      socketRef.current = socket;

      socket.onopen = () => {
        if (IS_DEV_MODE) console.log('Face detection socket connected');
        resetReconnect();
        setIsConnected(true);
        setIsConnecting(false);
      };

      socket.onclose = () => {
        if (IS_DEV_MODE) console.log('Face detection socket disconnected');
        setIsConnected(false);

        if (manualCloseRef.current || !autoConnectRef.current) {
          return;
        }

        schedule(() => connectRef.current());
      };

      socket.onerror = event => {
        console.error('Face detection socket error:', event);
        setIsConnected(false);
        setIsConnecting(false);
        setError(new Error('Face detection socket connection error'));
      };

      socket.onmessage = event => {
        const response = parseDetectionResponse(event.data);
        if (response) onMessageRef.current?.(response);
      };
    } catch (err) {
      console.error('Failed to create face detection socket connection:', err);
      setIsConnecting(false);
      setError(err instanceof Error ? err : new Error('Unknown socket error'));
    }
  }, [schedule, resetReconnect, onMessageRef, autoConnectRef, createSocketRef]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    manualCloseRef.current = true;
    resetReconnect();
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, [resetReconnect]);

  const sendData = useCallback((data: ArrayBuffer) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      socketRef.current.send(data);
      return true;
    } catch (err) {
      console.error('Error sending data over face detection socket:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (autoConnect) {
      connect();
    }
  }, [autoConnect, connect]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
    sendData,
  };
};
